<template>
	<button
		v-if="isCollapsed"
		type="button"
		class="question-entry"
		:aria-label="t('展开常见问题')"
		@click="toggleCollapse"
	>
		<span>{{ t("常见问题") }}</span>
	</button>
	<div v-else class="bazi-panel" :class="{ 'bazi-panel--no-profile': !showProfile }">
		<div v-if="showProfile" class="profile-row">
			<div class="profile-info">
				<img class="user-icon" :src="userAdd" width="32" height="32" alt="" />
				<span class="profile-name">{{ profile?.name || "" }}</span>
			</div>
			<div class="profile-actions">
				<button type="button" class="profile-btn" @click="goArchivePage">
					{{ profile?.name ? t("切换档案") : t("添加档案") }}
					<img :src="chevronRight" width="20" height="20" alt="" />
				</button>
				<button
					type="button"
					class="question-toggle"
					:aria-label="t('收起常见问题')"
					@click="toggleCollapse"
				>
					<img :src="faqCollapseBack" class="question-toggle__icon" width="24" height="24" alt="" />
				</button>
			</div>
		</div>
		<div class="question-list">
			<button
				v-for="(q, i) in questions"
				:key="i"
				type="button"
				class="question-chip"
				@click="onQuestion(q)"
			>
				{{ q }}
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { MasterSBaziFill } from "@variants/seer/master-s/shared/types/bazi-fill";
import type { UserMess } from "@variants/seer/master-s/shared/types/room-info";
import { chevronRight, faqCollapseBack, userAdd } from "@variants/seer/master-s/shared/images";
import { getQuestionList } from "../../../../../../api/master-s";
import { t } from "@variants/seer/master-s/shared/utils/i18n";
import showMessage from "../../../../../../utils/showMessage";

const props = withDefaults(
	defineProps<{
		userMess: UserMess | null;
		/** 未登录等场景可隐藏档案行，仅保留常见问题 */
		showProfile?: boolean;
		/** 变化时重新拉问题列表 / 档案（如 roomInfo 就绪 / 刷新后） */
		reloadKey?: number | string;
		collapsed?: boolean;
	}>(),
	{ showProfile: true, reloadKey: 0 }
);

const emit = defineEmits<{
	send: [text: string];
	profile: [profile: MasterSBaziFill];
	"update:collapsed": [value: boolean];
}>();

const questions = ref<string[]>([]);
const profile = ref<MasterSBaziFill | null>(null);
const isCollapsed = computed({
	get: () => Boolean(props.collapsed),
	set: (value: boolean) => emit("update:collapsed", value),
});

function applyUserMess(userMess: UserMess | null) {
	if (!userMess) {
		profile.value = null;
		return;
	}
	const sex = userMess.sex === 1 ? t("男") : t("女");
	const birthday = String(userMess.bir_timestamp || "").substring(0, 10);
	profile.value = {
		name: userMess.user_name,
		birthday,
		sex,
	};
	emit("profile", profile.value);
}

function applyBaziFile(
	file: {
		id?: number;
		user_name?: string;
		gender?: number;
		bir_timestamp?: string;
	} | null
) {
	if (!file?.user_name) return;
	applyUserMess({
		id: Number(file.id) || 0,
		sex: Number(file.gender) || 0,
		user_name: file.user_name,
		bir_timestamp: String(file.bir_timestamp || ""),
	});
}

async function loadQuestions() {
	try {
		const list = await getQuestionList(1);
		questions.value = Array.isArray(list) ? list.map(item => String(item)) : [];
	} catch (e) {
		console.error("getQuestionList failed", e);
		questions.value = [];
	}
}

async function loadProfile() {
	applyUserMess(props.userMess);
	if (!props.showProfile || !import.meta.client) return;
	if (!localStorage.getItem("token")) return;
	try {
		const store = useBaziFileStore();
		await store.getbaziFileList();
		applyBaziFile(store.baziFile);
	} catch (e) {
		console.error("getbaziFileList failed", e);
	}
}

async function reloadPanelData() {
	await Promise.all([loadProfile(), loadQuestions()]);
}

watch(
	() => props.reloadKey,
	() => {
		void reloadPanelData();
	},
	{ immediate: true }
);

watch(
	() => props.userMess,
	val => applyUserMess(val),
	{ deep: true }
);

function goArchivePage() {
	navigateTo({
		path: "/seer/user",
		query: { type: "default", from: "master-s" },
	});
}

function onQuestion(q: string) {
	if (props.showProfile && !profile.value?.name) {
		showMessage(t("请添加档案"), "warning");
		return;
	}
	emit("send", q);
}

function toggleCollapse() {
	isCollapsed.value = !isCollapsed.value;
}
</script>

<style scoped>
/* Figma 816:27601 八字档案区 309×116 → 750稿 ×2 */
.bazi-panel {
	width: 100%;
	box-sizing: border-box;
	padding: 20px 0 24px;
	border: 2px solid #fff;
	border-radius: 20px;
	background: linear-gradient(180deg, #fff8e9 0%, #fff 100%);
	overflow: hidden;
}

.bazi-panel--no-profile {
	padding-top: 24px;
}

.profile-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 16px;
	padding: 0 24px;
}

.profile-info {
	display: flex;
	align-items: center;
	gap: 6px;
	min-width: 0;
}

.user-icon {
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	object-fit: contain;
}

.profile-name {
	font-size: 28px;
	font-weight: 700;
	color: #333;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.profile-actions {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-shrink: 0;
}

.profile-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0;
	height: 44px;
	padding: 0 12px 0 20px;
	border: none;
	border-radius: 52px;
	background: #f2af19;
	color: #fff;
	font-size: 20px;
	font-weight: 700;
	line-height: 24px;
	white-space: nowrap;
	flex-shrink: 0;
}

.profile-btn img {
	display: block;
	width: 20px;
	height: 20px;
}

.question-toggle {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	padding: 0;
	border: 0;
	background: transparent;
	cursor: pointer;
}

.question-toggle__icon {
	display: block;
	width: 24px;
	height: 24px;
	object-fit: contain;
	transform: rotate(90deg);
}

.question-list {
	display: flex;
	flex-wrap: nowrap;
	gap: 16px;
	align-items: center;
	overflow-x: auto;
	padding: 0 24px;
	-webkit-overflow-scrolling: touch;
}

.question-entry {
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	height: 56px;
	padding: 0 16px;
	border: 2px solid #efefef;
	border-radius: 61px;
	background: rgba(255, 255, 255, 0.7);
	color: #333;
	font-size: 24px;
	font-weight: 400;
	line-height: 1;
	white-space: nowrap;
	cursor: pointer;
	box-sizing: border-box;
}

.question-chip {
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	padding: 16px;
	border: 1.63px solid #fff4dd;
	border-radius: 122px;
	background: rgba(255, 255, 255, 0.7);
	color: #333;
	font-size: 24px;
	font-weight: 400;
	line-height: 20px;
	white-space: nowrap;
	box-sizing: border-box;
}
</style>
